/**
 * Storyboard Continuity Audit — tiny authenticated asset thumbnail.
 *
 * Uses the SAME ?auth= query-string trick as ProductionContinuityPanel
 * (frontend/src/components/ProductionContinuityPanel.jsx::assetThumbUrl)
 * to load images via the existing `GET /api/assets/:id/stream` endpoint
 * since <img> tags cannot send Authorization headers.
 *
 * Zero new endpoints. Read-only. Gracefully renders a fallback square
 * if the asset is missing or the stream returns 404.
 *
 * Reference Image Preview / Lightbox Fix:
 *   - switched `object-cover` → `object-contain` so reference photos
 *     (typically portrait) are never cropped into thin strips.
 *   - optional `onOpen({ assetId, title })` callback wires the thumbnail
 *     to a parent-owned <ImageLightbox> modal. When provided, the
 *     thumbnail becomes a button with cursor-zoom-in + hover ring.
 */
import { useState } from "react";
import { ImageIcon } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ContinuityThumb({ assetId, size = 40, title, onOpen }) {
    const [errored, setErrored] = useState(false);
    if (!assetId) return null;

    const token = (typeof window !== "undefined")
        ? localStorage.getItem("aivos_token") : null;
    const qs = token ? `?auth=${encodeURIComponent(token)}` : "";
    const src = `${BACKEND}/api/assets/${assetId}/stream${qs}`;

    if (errored) {
        return (
            <span
                data-testid={`continuity-thumb-missing-${assetId.slice(0, 8)}`}
                title={title || assetId}
                style={{ width: size, height: size }}
                className="inline-flex items-center justify-center border border-zinc-800 bg-zinc-950 text-zinc-600 rounded-sm shrink-0"
            >
                <ImageIcon className="w-3.5 h-3.5" />
            </span>
        );
    }

    const img = (
        <img
            src={src}
            alt={title || assetId}
            title={title || assetId}
            loading="lazy"
            onError={() => setErrored(true)}
            data-testid={`continuity-thumb-${assetId.slice(0, 8)}`}
            style={{ width: size, height: size }}
            className="object-contain border border-zinc-800 rounded-sm shrink-0 bg-zinc-950"
        />
    );

    if (typeof onOpen === "function") {
        return (
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    onOpen({ assetId, title });
                }}
                data-testid={`continuity-thumb-open-${assetId.slice(0, 8)}`}
                title={title || assetId}
                aria-label={`Open preview · ${title || assetId}`}
                className="p-0 m-0 border-0 bg-transparent cursor-zoom-in hover:ring-1 hover:ring-emerald-500/40 rounded-sm shrink-0 transition"
            >
                {img}
            </button>
        );
    }

    return img;
}
